import * as graphics from "@client/app/graphics";
import * as forms from "@client/forms";
import * as typography from "@client/shared/typography";
import {Register} from "@client/site/components";
import {server} from "@reactivated";
import React from "react";

export type RegistrationJourneyProps = {
    close: (registered: server.core.rpc.Profile.output | null) => void;
};

export function RegistrationJourney(props: RegistrationJourneyProps) {
    const [isSaving, setIsSaving] = React.useState(false);
    const [isLogin, setIsLogin] = React.useState(false);

    const registrationForm = forms.useForm(server.core.rpc.Register, {
        initial: {invite_code: "i am not a robot"},
    });
    const loginForm = forms.useForm(server.core.rpc.Login);

    const register = async () => {
        setIsSaving(true);
        try {
            const response = await forms.submitForm(registrationForm, (data) =>
                server.core.rpc.register({
                    ...data,
                    with_notification: false,
                    recipe_uuid: null,
                    source: null,
                    platform: "web",
                }),
            );
            if (response?.type === "success") props.close(response.data);
        } finally {
            setIsSaving(false);
        }
    };

    const login = async () => {
        setIsSaving(true);
        try {
            const response = await forms.submitForm(loginForm, server.core.rpc.login);
            if (response?.type === "success") props.close(response.data);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLogin) {
        return (
            <form
                autoComplete="off"
                noValidate
                onSubmit={(event) => {
                    event.preventDefault();
                    void login();
                }}
                className="flex flex-col gap-4 p-6"
            >
                <h2 className="flex items-center gap-2 text-lg whitespace-nowrap">
                    <div className="size-4">
                        <graphics.Icon className="size-full" />
                    </div>
                    Login to Joy Health Tracker
                </h2>
                <div className="flex flex-col gap-4">
                    <forms.Field field={loginForm.fields.email} />
                    <forms.Field field={loginForm.fields.password} />
                    <typography.Button
                        submit
                        disabled={
                            loginForm.values.email == "" ||
                            loginForm.values.password == "" ||
                            isSaving
                        }
                    >
                        Login
                    </typography.Button>
                    <p className="text-sm/2">
                        Don’t have an account?{" "}
                        <button
                            className="font-semibold text-indigo-600"
                            onClick={() => setIsLogin(false)}
                        >
                            Create one
                        </button>
                        .
                    </p>
                    <p className="text-sm/2">
                        Forgot your password?{" "}
                        <a className="font-semibold text-indigo-600" href="/reset/">
                            Reset it
                        </a>
                    </p>
                </div>
            </form>
        );
    }

    return (
        <form
            autoComplete="off"
            noValidate
            onSubmit={(event) => {
                event.preventDefault();
                void register();
            }}
            className="flex flex-col gap-4 p-6"
        >
            <h2 className="flex items-center gap-2 text-lg whitespace-nowrap">
                <div className="size-4">
                    <graphics.Icon className="size-full" />
                </div>
                Sign up for Joy Health Tracker
            </h2>
            <p className="text-sm/6">
                Track your nutrition, workouts and habits.
                <br />
                All in <strong className="text-indigo-600">one</strong> place.
            </p>
            <forms.Field field={registrationForm.fields.email} />
            <Register isSubmitting={registrationForm.values.email == "" || isSaving} />

            <typography.P small muted>
                Joy is free for 7 days. Afterwards, it’s just 5 bucks a month.
            </typography.P>
            <typography.P small muted>
                Have an account?{" "}
                <button
                    className="font-semibold text-indigo-600"
                    onClick={() => setIsLogin(true)}
                >
                    Login
                </button>
            </typography.P>
        </form>
    );
}
